import {
  ApiError, Button, Card, EmailInput, ErrorState, Field, Input, InvitationIssuedPanel, LogoField, Notice, PageHeader,
  PhoneInput, Row, Select, Stack, Textarea, WeekendDaysPicker, normalisePhone, useApi, useI18n,
  type CreateTenantRequest, type ProvisionedTenantResponse, type TenantStatus, type TenantType,
} from '@daghfal/core';
import { useMutation, useQueryClient } from '@tanstack/react-query';
import { useState, type FormEvent } from 'react';
import { Link } from 'react-router-dom';

const TYPES: TenantType[] = ['Nursery', 'Kindergarten', 'Both'];

/** What a tenant may start as (`TenantLifecycle.InitialStatuses`): on a trial, or paying from the start (C6). */
const INITIAL_STATUSES: TenantStatus[] = ['Trial', 'Active'];

/**
 * Tenant provisioning (SRS §3.2, FR-PLT-05).
 *
 * The request creates the nursery and invites its owner in one step. The link goes to the owner's phone by
 * SMS and is shown here once to copy if the text does not arrive; a lost one is replaced from the tenant's
 * page. The platform never sets a password on the owner's behalf, and accepting needs a code sent to the
 * owner's phone. The link's address comes from the server's configuration, not from this screen.
 */
export function NewTenant() {
  const api = useApi();
  const queryClient = useQueryClient();
  const { t } = useI18n();

  const [form, setForm] = useState<CreateTenantRequest>({
    nameAr: '', nameEn: '', slug: '',
    type: 'Nursery',
    country: 'SY',
    timeZone: 'Asia/Damascus',
    locale: 'ar',
    currency: 'SYP',
    // Friday and Saturday, as tenant #1 runs; this drives absence calculation, so it is set at creation
    // rather than left to a default buried in the server (SRS §2.4).
    weekendDays: ['Friday', 'Saturday'],
    contactPhone: '', contactEmail: '', address: '',
    planId: null,
    initialStatus: 'Trial',
    ownerFullName: '', ownerPhone: '', ownerEmail: '',
  });
  const [ownerPhoneError, setOwnerPhoneError] = useState<string | null>(null);
  const [logo, setLogo] = useState<Blob | null>(null);

  const create = useMutation({
    mutationFn: async (body: CreateTenantRequest): Promise<Provisioning> => {
      const provisioned = await api.platform.createTenant(body);
      // The logo needs the tenant to exist first. Once it does, a failed upload must not keep the form
      // open: sending it again would try to provision the nursery a second time.
      const logoFailed = logo !== null
        && await api.platform.setTenantLogo(provisioned.tenant.id, logo).then(() => false, () => true);
      return { provisioned, logoFailed };
    },
    onSuccess: () => {
      void queryClient.invalidateQueries({ queryKey: ['platform', 'tenants'] });
      void queryClient.invalidateQueries({ queryKey: ['platform', 'dashboard'] });
    },
  });

  const set = <K extends keyof CreateTenantRequest>(key: K, value: CreateTenantRequest[K]) =>
    setForm((current) => ({ ...current, [key]: value }));

  function submit(event: FormEvent) {
    event.preventDefault();

    const ownerPhone = normalisePhone(form.ownerPhone);
    if (!ownerPhone) { setOwnerPhoneError(t('tenants.new.ownerPhone.invalid')); return; }
    setOwnerPhoneError(null);

    create.mutate({
      ...form,
      ownerPhone,
      // Empty optional text is sent as null rather than "", so the API stores an absent value rather than
      // a blank one and `contactEmail ?? '—'` on the profile reads correctly.
      nameEn: form.nameEn?.trim() || null,
      contactPhone: normalisePhone(form.contactPhone ?? ''),
      contactEmail: form.contactEmail?.trim() || null,
      address: form.address?.trim() || null,
      ownerEmail: form.ownerEmail?.trim() || null,
    });
  }

  if (create.data) {
    return (
      <Provisioned
        result={create.data.provisioned}
        logoFailed={create.data.logoFailed}
        onAnother={() => { setLogo(null); create.reset(); }}
      />
    );
  }

  // The API returns per-field messages for a rejected slug or name; they belong next to the input.
  const fieldErrors = create.error instanceof ApiError ? create.error.fieldErrors : {};

  return (
    <>
      <PageHeader
        title={t('tenants.new.title')}
        lede={t('tenants.new.lede')}
        actions={<Link className="dg-button dg-button--ghost" to="/tenants">{t('action.cancel')}</Link>}
      />

      <form onSubmit={submit} noValidate>
        <Stack>
          {create.isError && <ErrorState error={create.error} />}

          <Card title={t('tenants.title')}>
            <Stack>
              <Field label={t('tenants.new.nameAr')} error={fieldErrors.nameAr} required>
                {(props) => (
                  <Input {...props} value={form.nameAr} onChange={(e) => set('nameAr', e.target.value)} autoFocus />
                )}
              </Field>

              <Field label={t('tenants.new.nameEn')} error={fieldErrors.nameEn}>
                {(props) => (
                  <Input {...props} dir="ltr" value={form.nameEn ?? ''} onChange={(e) => set('nameEn', e.target.value)} />
                )}
              </Field>

              <Field
                label={t('tenants.new.slug')}
                hint={t('tenants.new.slug.hint', { slug: form.slug || '…' })}
                error={fieldErrors.slug}
                required
              >
                {(props) => (
                  <Input
                    {...props}
                    dir="ltr"
                    value={form.slug}
                    // The slug is part of a URL path and is normalised as it is typed, so what the field
                    // shows is exactly what the entry link will be (decision 2026-10-14 #2).
                    onChange={(e) => set('slug', e.target.value.toLowerCase().replace(/[^a-z0-9-]/g, '-'))}
                  />
                )}
              </Field>

              <Field label={t('tenants.new.type')} required>
                {(props) => (
                  <Select {...props} value={form.type} onChange={(e) => set('type', e.target.value as TenantType)}>
                    {TYPES.map((type) => (
                      <option key={type} value={type}>{t(`tenants.new.type.${type}`)}</option>
                    ))}
                  </Select>
                )}
              </Field>

              <Row>
                <Field label={t('tenants.new.country')}>
                  {(props) => (
                    <Input {...props} dir="ltr" value={form.country ?? ''} onChange={(e) => set('country', e.target.value)} />
                  )}
                </Field>
                <Field label={t('tenants.new.timeZone')} required>
                  {(props) => (
                    <Input {...props} dir="ltr" value={form.timeZone} onChange={(e) => set('timeZone', e.target.value)} />
                  )}
                </Field>
                <Field label={t('tenants.new.currency')} required>
                  {(props) => (
                    <Input {...props} dir="ltr" value={form.currency} onChange={(e) => set('currency', e.target.value.toUpperCase())} />
                  )}
                </Field>
              </Row>

              <Field label={t('tenants.new.contactPhone')}>
                {(props) => (
                  <PhoneInput {...props} value={form.contactPhone ?? ''} onChange={(e) => set('contactPhone', e.target.value)} />
                )}
              </Field>

              <Field label={t('tenants.new.contactEmail')}>
                {(props) => (
                  <Input {...props} type="email" dir="ltr" value={form.contactEmail ?? ''} onChange={(e) => set('contactEmail', e.target.value)} />
                )}
              </Field>

              <Field label={t('identity.address')} error={fieldErrors.address}>
                {(props) => (
                  <Textarea {...props} rows={2} value={form.address ?? ''} onChange={(e) => set('address', e.target.value)} />
                )}
              </Field>

              <WeekendDaysPicker value={form.weekendDays ?? []} onChange={(days) => set('weekendDays', days)} />

              <Field label={t('tenants.new.initialStatus')} hint={t('tenants.new.initialStatus.hint')}>
                {(props) => (
                  <Select
                    {...props}
                    value={form.initialStatus ?? 'Trial'}
                    onChange={(e) => set('initialStatus', e.target.value as TenantStatus)}
                  >
                    {INITIAL_STATUSES.map((status) => (
                      <option key={status} value={status}>{t(`tenants.new.initialStatus.${status}`)}</option>
                    ))}
                  </Select>
                )}
              </Field>
            </Stack>
          </Card>

          <Card title={t('logo.title')}>
            <LogoField name={form.nameAr} value={logo} onChange={setLogo} hint={t('tenants.new.logo.hint')} />
          </Card>

          <Card title={t('tenants.new.owner')}>
            <Stack>
              <Field label={t('tenants.new.ownerName')} error={fieldErrors.ownerFullName} required>
                {(props) => (
                  <Input {...props} value={form.ownerFullName} onChange={(e) => set('ownerFullName', e.target.value)} />
                )}
              </Field>
              <Field
                label={t('tenants.new.ownerPhone')}
                hint={t('auth.phone.hint')}
                error={ownerPhoneError ?? fieldErrors.ownerPhone}
                required
              >
                {(props) => (
                  <PhoneInput {...props} value={form.ownerPhone} onChange={(e) => set('ownerPhone', e.target.value)} />
                )}
              </Field>
              <Field label={t('invitation.email')} hint={t('invitation.email.hint')}>
                {(props) => (
                  <EmailInput
                    {...props}
                    value={form.ownerEmail ?? ''}
                    onChange={(e) => set('ownerEmail', e.target.value)}
                  />
                )}
              </Field>
            </Stack>
          </Card>

          <Row>
            <Button type="submit" size="large" busy={create.isPending} busyLabel={t('action.saving')}>
              {t('tenants.new.submit')}
            </Button>
          </Row>
        </Stack>
      </form>
    </>
  );
}

/** What provisioning produced, and whether the logo chosen with it failed to upload. */
interface Provisioning {
  provisioned: ProvisionedTenantResponse;
  logoFailed: boolean;
}

/** The one and only showing of the owner's invitation link. */
function Provisioned({ result, logoFailed, onAnother }: {
  result: ProvisionedTenantResponse; logoFailed: boolean; onAnother: () => void;
}) {
  const { t } = useI18n();

  return (
    <>
      <PageHeader title={t('tenants.new.created', { name: result.tenant.nameAr })} />

      {logoFailed && <Notice tone="warning">{t('tenants.new.logoFailed')}</Notice>}

      <InvitationIssuedPanel invitation={result.ownerInvitation} />

      <Row>
        <Link className="dg-button" to={`/tenants/${result.tenant.id}`}>{t('tenants.new.goToTenant')}</Link>
        <Button variant="ghost" onClick={onAnother}>{t('tenants.new')}</Button>
      </Row>
    </>
  );
}
